/**
 * IAM Permission System
 * 
 * Centralized permission definitions and utilities for granular access control.
 * Permissions are organized by resource type (pages, features, actions).
 */

// ==================== PERMISSION CONSTANTS ====================

/**
 * Page-level permissions - control access to entire pages
 */
export const PAGE_PERMISSIONS = {
  // Core Pages
  DASHBOARD: 'page:dashboard',
  CUSTOMERS: 'page:customers',
  OPPORTUNITIES: 'page:opportunities',
  ORDERS: 'page:orders',
  CONTACTS: 'page:contacts',
  TASKS: 'page:tasks',
  REPORTS: 'page:reports',
  
  // Email Marketing Pages
  EMAIL_CAMPAIGNS: 'page:email_campaigns',
  EMAIL_TEMPLATES: 'page:email_templates',
  EMAIL_SEGMENTS: 'page:email_segments',
  EMAIL_ANALYTICS: 'page:email_analytics',
  
  // Calendar
  CALENDAR: 'page:calendar',
  
  // AI Sales Agent add-on
  RECOMMENDATIONS: 'page:recommendations',
  
  // Admin Pages
  USER_ADMIN: 'page:user_admin',
  ROLE_MANAGEMENT: 'page:role_management',
  SALES_REPS: 'page:sales_reps',
};

/**
 * Feature-level permissions - control specific features within pages
 */
export const FEATURE_PERMISSIONS = {
  // Customer Features
  CUSTOMER_CREATE: 'feature:CUSTOMER_CREATE',
  CUSTOMER_EDIT: 'feature:CUSTOMER_EDIT',
  CUSTOMER_DELETE: 'feature:CUSTOMER_DELETE',
  CUSTOMER_IMPORT: 'feature:CUSTOMER_IMPORT',
  CUSTOMER_EXPORT: 'feature:CUSTOMER_EXPORT',
  CUSTOMER_MERGE: 'feature:CUSTOMER_MERGE',
  CUSTOMER_VIEW_ALL: 'feature:CUSTOMER_VIEW_ALL', // vs only assigned
  
  // Opportunity Features
  OPP_CREATE: 'feature:opp_create',
  OPP_EDIT: 'feature:opp_edit',
  OPP_DELETE: 'feature:opp_delete',
  OPP_IMPORT: 'feature:opp_import',
  OPP_EXPORT: 'feature:opp_export',
  OPP_VIEW_ALL: 'feature:opp_view_all',
  OPP_FOLLOWUP_MANAGE: 'feature:opp_followup_manage', // Set/clear re-engagement follow-up flags on Not Qualified / Closed Lost opportunities
  
  // Order Features
  ORDER_CREATE: 'feature:order_create',
  ORDER_EDIT: 'feature:order_edit',
  ORDER_DELETE: 'feature:order_delete',
  ORDER_IMPORT: 'feature:order_import',
  ORDER_EXPORT: 'feature:order_export',
  ORDER_VIEW_ALL: 'feature:order_view_all',
  ORDER_VIEW_FINANCIALS: 'feature:order_view_financials', // commissions, costs
  
  // Contact Features
  CONTACT_CREATE: 'feature:contact_create',
  CONTACT_EDIT: 'feature:contact_edit',
  CONTACT_DELETE: 'feature:contact_delete',
  CONTACT_IMPORT: 'feature:contact_import',
  CONTACT_EXPORT: 'feature:contact_export',
  CONTACT_MERGE: 'feature:contact_merge',
  CONTACT_VIEW_ALL: 'feature:contact_view_all', // vs only assigned contacts
  
  // Task Features
  TASK_CREATE: 'feature:task_create',
  TASK_EDIT: 'feature:task_edit',
  TASK_DELETE: 'feature:task_delete',
  TASK_VIEW_ALL: 'feature:task_view_all',
  TASK_ASSIGN_ANY: 'feature:task_assign_any', // Assign tasks to any user (not just self). Admin + customer support use case.
  
  // Task Features (continued)
  TASK_EXPORT: 'feature:task_export',
  
  // Calendar Features
  CALENDAR_CREATE: 'feature:calendar_create',
  CALENDAR_EDIT: 'feature:calendar_edit',
  CALENDAR_DELETE: 'feature:calendar_delete',
  CALENDAR_VIEW_ALL: 'feature:calendar_view_all',
  
  // Email Campaign Features
  EMAIL_CREATE_CAMPAIGN: 'feature:email_create_campaign',
  EMAIL_EDIT_CAMPAIGN: 'feature:email_edit_campaign',
  EMAIL_DELETE_CAMPAIGN: 'feature:email_delete_campaign',
  EMAIL_DELETE_SENT_CAMPAIGN: 'feature:email_delete_sent_campaign',
  EMAIL_SEND_CAMPAIGN: 'feature:email_send_campaign',
  EMAIL_VIEW_ALL: 'feature:email_view_all',
  
  // Email Template Features
  EMAIL_CREATE_TEMPLATE: 'feature:email_create_template',
  EMAIL_EDIT_TEMPLATE: 'feature:email_edit_template',
  EMAIL_DELETE_TEMPLATE: 'feature:email_delete_template',
  
  // Email Segment Features
  EMAIL_CREATE_SEGMENT: 'feature:email_create_segment',
  EMAIL_EDIT_SEGMENT: 'feature:email_edit_segment',
  EMAIL_DELETE_SEGMENT: 'feature:email_delete_segment',
  
  // Email Analytics Features
  EMAIL_VIEW_ANALYTICS: 'feature:email_view_analytics',
  
  // Workflow Automation Features
  WORKFLOWS_MANAGE: 'feature:workflows_manage',
  WORKFLOWS_VIEW: 'feature:workflows_view',
  
  // Chatbot Features
  CHATBOT_VIEW: 'feature:chatbot_view',
  CHATBOT_MANAGE: 'feature:chatbot_manage',
  
  // Knowledge Management Features
  KNOWLEDGE_VIEW: 'feature:knowledge_view',
  KNOWLEDGE_MANAGE: 'feature:knowledge_manage',
  
  // Achievement / Gamification Features
  ACHIEVEMENTS_VIEW: 'feature:achievements_view',
  ACHIEVEMENTS_MANAGE: 'feature:achievements_manage',
  
  // Log Management Features
  LOGS_VIEW: 'feature:logs_view',
  LOGS_MANAGE: 'feature:logs_manage',
  
  // Notes Features (cross-entity)
  NOTE_ADD: 'feature:note_add', // Add notes to any entity without full entity edit permission
  NOTE_DELETE_ANY: 'feature:note_delete_any', // Delete any user's notes (admin privilege)
  
  // Notification Settings Features
  NOTIFICATIONS_VIEW: 'feature:notifications_view',
  NOTIFICATIONS_MANAGE: 'feature:notifications_manage',
  
  // User Management Features
  USER_CREATE: 'feature:user_create',
  USER_EDIT: 'feature:user_edit',
  USER_DELETE: 'feature:user_delete',
  USER_CHANGE_ROLE: 'feature:user_change_role',
  USER_MANAGE_PERMISSIONS: 'feature:user_manage_permissions',
  USER_MANAGE_ATTRIBUTES: 'feature:user_manage_attributes', // Edit Title, Department, Display Name
  USER_MANAGE_TERRITORIES: 'feature:user_manage_territories', // Create territories and assign them to sales reps (ADMIN ONLY)
  USER_VIEW_ALL: 'feature:user_view_all', // View all users vs only self
  
  // Role Management Features
  ROLE_CREATE: 'feature:role_create',
  ROLE_EDIT: 'feature:role_edit',
  ROLE_DELETE: 'feature:role_delete',
  
  // Report Features
  REPORTS_VIEW_ALL_REPS: 'feature:reports_view_all_reps',
  REPORTS_EXPORT: 'feature:reports_export',
  REPORTS_CACHE_REFRESH: 'feature:reports_cache_refresh',
  
  // Sales Rep Features
  SALES_REP_VIEW_ALL: 'feature:sales_rep_view_all', // View all sales rep data
  
  // Settings Features
  SETTINGS_VIEW: 'feature:settings_view', // View system settings (read-only)
  SETTINGS_MANAGE: 'feature:settings_manage', // Manage system settings (notifications, etc.)
  
  // System Settings Features
  SYSTEM_SETTINGS_VIEW: 'feature:system_settings_view', // View email templates, script variables, configs (read-only)
  SYSTEM_SETTINGS_MANAGE: 'feature:system_settings_manage', // Manage email templates, script variables, configs

  // Data Sync Features
  DATA_SYNC_VIEW: 'feature:data_sync_view', // View sync status and logs (read-only)
  DATA_SYNC_TRIGGER: 'feature:data_sync_trigger', // Trigger quick and full syncs
  DATA_SYNC_HISTORICAL: 'feature:data_sync_historical', // Trigger historical (force-write) syncs

  // AI Sales Agent add-on (also requires config/addons.aiSalesAgent.enabled)
  AI_RECS_VIEW: 'feature:ai_recs_view', // See Recommendations tab + own queue
  AI_RECS_ACT: 'feature:ai_recs_act', // Accept / dismiss / snooze recommendations
  AI_RECS_GENERATE: 'feature:ai_recs_generate', // On-demand refresh
  AI_RECS_VIEW_TEAM: 'feature:ai_recs_view_team', // Team-wide queue (all reps)
  QUOTES_BUILD: 'feature:quotes_build', // Phase 6 quote builder (also needs Platform Controls quotingEnabled)

};

/**
 * Action-level permissions - fine-grained control for specific actions
 */
export const ACTION_PERMISSIONS = {
  // Logging & Audit
  VIEW_CHANGE_LOGS: 'action:view_change_logs',
  VIEW_USER_ACTIVITY: 'action:view_user_activity',
  
  // Data Management
  BULK_OPERATIONS: 'action:bulk_operations',
  DESTRUCTIVE_IMPORT: 'action:destructive_import', // import with delete
  
  // System Administration
  MANAGE_BUSINESS_TYPES: 'action:manage_business_types',
  MANAGE_LEAD_SOURCES: 'action:manage_lead_sources',
  MANAGE_CATEGORIES: 'action:manage_categories',
  
  // Integration & API
  API_ACCESS: 'action:api_access',
};

/**
 * All permissions combined
 */
export const ALL_PERMISSIONS = {
  ...PAGE_PERMISSIONS,
  ...FEATURE_PERMISSIONS,
  ...ACTION_PERMISSIONS,
};

/**
 * Maps each page permission to the feature permissions that belong within that page.
 * Used for granular RBAC: if a user has any accessible feature permission for a page,
 * they implicitly gain access to that page even if the page-level permission is denied
 * or not in their role. This ensures feature grants are never silently blocked by a
 * missing or denied parent page permission.
 */
export const PAGE_FEATURE_MAP = {
  [PAGE_PERMISSIONS.CUSTOMERS]: [
    FEATURE_PERMISSIONS.CUSTOMER_CREATE,
    FEATURE_PERMISSIONS.CUSTOMER_EDIT,
    FEATURE_PERMISSIONS.CUSTOMER_DELETE,
    FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL,
    FEATURE_PERMISSIONS.CUSTOMER_IMPORT,
    FEATURE_PERMISSIONS.CUSTOMER_EXPORT,
    FEATURE_PERMISSIONS.CUSTOMER_MERGE,
  ],
  [PAGE_PERMISSIONS.OPPORTUNITIES]: [
    FEATURE_PERMISSIONS.OPP_CREATE,
    FEATURE_PERMISSIONS.OPP_EDIT,
    FEATURE_PERMISSIONS.OPP_DELETE,
    FEATURE_PERMISSIONS.OPP_VIEW_ALL,
    FEATURE_PERMISSIONS.OPP_IMPORT,
    FEATURE_PERMISSIONS.OPP_EXPORT,
    FEATURE_PERMISSIONS.OPP_FOLLOWUP_MANAGE,
  ],
  [PAGE_PERMISSIONS.ORDERS]: [
    FEATURE_PERMISSIONS.ORDER_CREATE,
    FEATURE_PERMISSIONS.ORDER_EDIT,
    FEATURE_PERMISSIONS.ORDER_DELETE,
    FEATURE_PERMISSIONS.ORDER_VIEW_ALL,
    FEATURE_PERMISSIONS.ORDER_VIEW_FINANCIALS,
    FEATURE_PERMISSIONS.ORDER_IMPORT,
    FEATURE_PERMISSIONS.ORDER_EXPORT,
  ],
  [PAGE_PERMISSIONS.CONTACTS]: [
    FEATURE_PERMISSIONS.CONTACT_CREATE,
    FEATURE_PERMISSIONS.CONTACT_EDIT,
    FEATURE_PERMISSIONS.CONTACT_DELETE,
    FEATURE_PERMISSIONS.CONTACT_VIEW_ALL,
    FEATURE_PERMISSIONS.CONTACT_IMPORT,
    FEATURE_PERMISSIONS.CONTACT_EXPORT,
    FEATURE_PERMISSIONS.CONTACT_MERGE,
  ],
  [PAGE_PERMISSIONS.TASKS]: [
    FEATURE_PERMISSIONS.TASK_CREATE,
    FEATURE_PERMISSIONS.TASK_EDIT,
    FEATURE_PERMISSIONS.TASK_DELETE,
    FEATURE_PERMISSIONS.TASK_EXPORT,
    FEATURE_PERMISSIONS.TASK_VIEW_ALL,
    FEATURE_PERMISSIONS.TASK_ASSIGN_ANY,
  ],
  [PAGE_PERMISSIONS.CALENDAR]: [
    FEATURE_PERMISSIONS.CALENDAR_CREATE,
    FEATURE_PERMISSIONS.CALENDAR_EDIT,
    FEATURE_PERMISSIONS.CALENDAR_DELETE,
    FEATURE_PERMISSIONS.CALENDAR_VIEW_ALL,
  ],
  [PAGE_PERMISSIONS.REPORTS]: [
    FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS,
    FEATURE_PERMISSIONS.REPORTS_EXPORT,
    FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH,
  ],
  [PAGE_PERMISSIONS.SALES_REPS]: [
    FEATURE_PERMISSIONS.SALES_REP_VIEW_ALL,
  ],
  [PAGE_PERMISSIONS.EMAIL_CAMPAIGNS]: [
    FEATURE_PERMISSIONS.EMAIL_CREATE_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_EDIT_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_DELETE_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_DELETE_SENT_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_SEND_CAMPAIGN,
  ],
  [PAGE_PERMISSIONS.EMAIL_TEMPLATES]: [
    FEATURE_PERMISSIONS.EMAIL_CREATE_TEMPLATE,
    FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE,
    FEATURE_PERMISSIONS.EMAIL_DELETE_TEMPLATE,
  ],
  [PAGE_PERMISSIONS.EMAIL_SEGMENTS]: [
    FEATURE_PERMISSIONS.EMAIL_CREATE_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_EDIT_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_DELETE_SEGMENT,
  ],
  [PAGE_PERMISSIONS.EMAIL_ANALYTICS]: [
    FEATURE_PERMISSIONS.EMAIL_VIEW_ANALYTICS,
  ],
  [PAGE_PERMISSIONS.USER_ADMIN]: [
    FEATURE_PERMISSIONS.USER_CREATE,
    FEATURE_PERMISSIONS.USER_EDIT,
    FEATURE_PERMISSIONS.USER_DELETE,
    FEATURE_PERMISSIONS.USER_CHANGE_ROLE,
    FEATURE_PERMISSIONS.USER_MANAGE_PERMISSIONS,
    FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES,
    FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES,
    FEATURE_PERMISSIONS.USER_VIEW_ALL,
  ],
  [PAGE_PERMISSIONS.ROLE_MANAGEMENT]: [
    FEATURE_PERMISSIONS.ROLE_CREATE,
    FEATURE_PERMISSIONS.ROLE_EDIT,
    FEATURE_PERMISSIONS.ROLE_DELETE,
  ],
  [PAGE_PERMISSIONS.RECOMMENDATIONS]: [
    FEATURE_PERMISSIONS.AI_RECS_VIEW,
    FEATURE_PERMISSIONS.AI_RECS_ACT,
    FEATURE_PERMISSIONS.AI_RECS_GENERATE,
    FEATURE_PERMISSIONS.AI_RECS_VIEW_TEAM,
    FEATURE_PERMISSIONS.QUOTES_BUILD,
  ],
};

// ==================== PERMISSION GROUPS ====================

/**
 * Logical grouping of permissions for UI organization
 */
export const PERMISSION_GROUPS = [
  {
    label: 'Core Pages',
    description: 'Access to main application pages',
    permissions: [
      { key: PAGE_PERMISSIONS.DASHBOARD, label: 'Dashboard', description: 'Access the dashboard page' },
      { key: PAGE_PERMISSIONS.CUSTOMERS, label: 'Customers', description: 'Access the customers page' },
      { key: PAGE_PERMISSIONS.OPPORTUNITIES, label: 'Opportunities', description: 'Access the opportunities page' },
      { key: PAGE_PERMISSIONS.ORDERS, label: 'Orders', description: 'Access the orders page' },
      { key: PAGE_PERMISSIONS.CONTACTS, label: 'Contacts', description: 'Access the contacts page' },
      { key: PAGE_PERMISSIONS.TASKS, label: 'Tasks', description: 'Access the tasks page' },
      { key: PAGE_PERMISSIONS.REPORTS, label: 'Reports', description: 'Access the reports page' },
      { key: PAGE_PERMISSIONS.SALES_REPS, label: 'Sales Reps', description: 'Access the sales reps page' },
      { key: PAGE_PERMISSIONS.CALENDAR, label: 'Calendar', description: 'Access the calendar page' },
    ],
  },
  {
    label: 'Calendar Management',
    description: 'Calendar event operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.CALENDAR_CREATE, label: 'Create Events', description: 'Create new calendar events' },
      { key: FEATURE_PERMISSIONS.CALENDAR_EDIT, label: 'Edit Events', description: 'Modify calendar events' },
      { key: FEATURE_PERMISSIONS.CALENDAR_DELETE, label: 'Delete Events', description: 'Remove calendar events' },
      { key: FEATURE_PERMISSIONS.CALENDAR_VIEW_ALL, label: 'View All Events', description: 'See all users\' calendar events (ADMIN ONLY)' },
    ],
  },
  {
    label: 'Email Marketing — Page Access',
    description: 'Access to email marketing pages (navigate to them)',
    permissions: [
      { key: PAGE_PERMISSIONS.EMAIL_CAMPAIGNS, label: 'Campaigns Page', description: 'Access the Campaigns page' },
      { key: PAGE_PERMISSIONS.EMAIL_TEMPLATES, label: 'Templates Page', description: 'Access the Templates page' },
      { key: PAGE_PERMISSIONS.EMAIL_SEGMENTS, label: 'Segments Page', description: 'Access the Segments page' },
      { key: PAGE_PERMISSIONS.EMAIL_ANALYTICS, label: 'Analytics Page', description: 'Access the Email Analytics page' },
    ],
  },
  {
    label: 'Email Campaigns',
    description: 'Campaign management operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.EMAIL_CREATE_CAMPAIGN, label: 'Create Campaigns', description: 'Create new email campaigns' },
      { key: FEATURE_PERMISSIONS.EMAIL_EDIT_CAMPAIGN, label: 'Edit Campaigns', description: 'Modify existing campaigns' },
      { key: FEATURE_PERMISSIONS.EMAIL_DELETE_CAMPAIGN, label: 'Delete Campaigns', description: 'Delete/archive campaigns' },
      { key: FEATURE_PERMISSIONS.EMAIL_DELETE_SENT_CAMPAIGN, label: 'Delete Sent Campaigns', description: 'Delete sent campaigns and their email events (off by default)' },
      { key: FEATURE_PERMISSIONS.EMAIL_SEND_CAMPAIGN, label: 'Send Campaigns', description: 'Send or schedule campaigns for delivery' },
      { key: FEATURE_PERMISSIONS.EMAIL_VIEW_ALL, label: 'Tenant-wide Email Marketing', description: 'Access all campaigns, segments and customer audiences. Without this, email access is limited to your campaigns, segments and currently assigned customers. Admin, manager and marketing retain tenant-wide access unless explicitly denied.' },
    ],
  },
  {
    label: 'Email Templates',
    description: 'Template management operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.EMAIL_CREATE_TEMPLATE, label: 'Create Templates', description: 'Create new email templates' },
      { key: FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE, label: 'Edit Templates', description: 'Modify email templates' },
      { key: FEATURE_PERMISSIONS.EMAIL_DELETE_TEMPLATE, label: 'Delete Templates', description: 'Remove email templates' },
    ],
  },
  {
    label: 'Email Segments',
    description: 'Segment management operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.EMAIL_CREATE_SEGMENT, label: 'Create Segments', description: 'Create audience segments' },
      { key: FEATURE_PERMISSIONS.EMAIL_EDIT_SEGMENT, label: 'Edit Segments', description: 'Modify existing segments' },
      { key: FEATURE_PERMISSIONS.EMAIL_DELETE_SEGMENT, label: 'Delete Segments', description: 'Remove audience segments' },
    ],
  },
  {
    label: 'Email Analytics',
    description: 'Email analytics viewing',
    permissions: [
      { key: FEATURE_PERMISSIONS.EMAIL_VIEW_ANALYTICS, label: 'View Email Analytics', description: 'View campaign analytics data (also grants access to Analytics page)' },
    ],
  },
  {
    label: 'Customer Management',
    description: 'Customer-related operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.CUSTOMER_CREATE, label: 'Create customers', description: 'Add new customers' },
      { key: FEATURE_PERMISSIONS.CUSTOMER_EDIT, label: 'Edit customers', description: 'Modify customer details' },
      { key: FEATURE_PERMISSIONS.CUSTOMER_DELETE, label: 'Delete customers', description: 'Remove customers' },
      { key: FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL, label: 'View All customers', description: 'See all customers (not just assigned) (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.CUSTOMER_IMPORT, label: 'Import customers', description: 'Import customer data' },
      { key: FEATURE_PERMISSIONS.CUSTOMER_EXPORT, label: 'Export customers', description: 'Export customer data' },
      { key: FEATURE_PERMISSIONS.CUSTOMER_MERGE, label: 'Merge customers', description: 'Merge duplicate customers' },
    ],
  },
  {
    label: 'Opportunity Management',
    description: 'Opportunity-related operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.OPP_CREATE, label: 'Create Opportunities', description: 'Add new opportunities' },
      { key: FEATURE_PERMISSIONS.OPP_EDIT, label: 'Edit Opportunities', description: 'Modify opportunities' },
      { key: FEATURE_PERMISSIONS.OPP_DELETE, label: 'Delete Opportunities', description: 'Remove opportunities' },
      { key: FEATURE_PERMISSIONS.OPP_VIEW_ALL, label: 'View All Opportunities', description: 'See all opportunities (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.OPP_IMPORT, label: 'Import Opportunities', description: 'Import opportunity data' },
      { key: FEATURE_PERMISSIONS.OPP_EXPORT, label: 'Export Opportunities', description: 'Export opportunity data' },
      { key: FEATURE_PERMISSIONS.OPP_FOLLOWUP_MANAGE, label: 'Manage Follow-Up Flags', description: 'Set re-engagement follow-up flags on Not Qualified / Closed Lost opportunities' },
    ],
  },
  {
    label: 'Order Management',
    description: 'Order-related operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.ORDER_CREATE, label: 'Create Orders', description: 'Add new orders' },
      { key: FEATURE_PERMISSIONS.ORDER_EDIT, label: 'Edit Orders', description: 'Modify orders' },
      { key: FEATURE_PERMISSIONS.ORDER_DELETE, label: 'Delete Orders', description: 'Remove orders' },
      { key: FEATURE_PERMISSIONS.ORDER_VIEW_ALL, label: 'View All Orders', description: 'See all orders (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.ORDER_VIEW_FINANCIALS, label: 'View Financials', description: 'See commissions and costs' },
      { key: FEATURE_PERMISSIONS.ORDER_IMPORT, label: 'Import Orders', description: 'Import order data' },
      { key: FEATURE_PERMISSIONS.ORDER_EXPORT, label: 'Export Orders', description: 'Export order data' },
    ],
  },
  {
    label: 'Contact Management',
    description: 'Contact-related operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.CONTACT_CREATE, label: 'Create Contacts', description: 'Add new contacts' },
      { key: FEATURE_PERMISSIONS.CONTACT_EDIT, label: 'Edit Contacts', description: 'Modify contacts' },
      { key: FEATURE_PERMISSIONS.CONTACT_DELETE, label: 'Delete Contacts', description: 'Remove contacts' },
      { key: FEATURE_PERMISSIONS.CONTACT_VIEW_ALL, label: 'View All Contacts', description: 'See all contacts (not just assigned) (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.CONTACT_IMPORT, label: 'Import Contacts', description: 'Import contact data' },
      { key: FEATURE_PERMISSIONS.CONTACT_EXPORT, label: 'Export Contacts', description: 'Export contact data' },
      { key: FEATURE_PERMISSIONS.CONTACT_MERGE, label: 'Merge Contacts', description: 'Merge duplicate contacts' },
    ],
  },
  {
    label: 'Task Management',
    description: 'Task-related operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.TASK_CREATE, label: 'Create Tasks', description: 'Add new tasks' },
      { key: FEATURE_PERMISSIONS.TASK_EDIT, label: 'Edit Tasks', description: 'Modify tasks' },
      { key: FEATURE_PERMISSIONS.TASK_DELETE, label: 'Delete Tasks', description: 'Remove tasks' },
      { key: FEATURE_PERMISSIONS.TASK_EXPORT, label: 'Export Tasks', description: 'Export task data' },
      { key: FEATURE_PERMISSIONS.TASK_VIEW_ALL, label: 'View All Tasks', description: 'See all user tasks (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.TASK_ASSIGN_ANY, label: 'Assign Tasks to Any User', description: 'Assign tasks to any user (not just self). Without this, users can only assign to themselves.' },
    ],
  },
  {
    label: 'Reports & Analytics',
    description: 'Reporting features',
    permissions: [
      { key: FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS, label: 'View All Sales Reps Performance', description: 'See all sales rep performance (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.REPORTS_EXPORT, label: 'Export Reports', description: 'Export report data' },
      { key: FEATURE_PERMISSIONS.SALES_REP_VIEW_ALL, label: 'View All Sales Reps', description: 'See all sales rep performance (not just logged in user) (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH, label: 'Refresh Cache', description: 'Manually refresh reports cache' },
    ],
  },
  {
    label: 'User Administration',
    description: 'User and role management',
    permissions: [
      { key: PAGE_PERMISSIONS.USER_ADMIN, label: 'User Admin Page', description: 'Access the user administration page' },
      { key: FEATURE_PERMISSIONS.USER_CREATE, label: 'Create Users', description: 'Add new users' },
      { key: FEATURE_PERMISSIONS.USER_EDIT, label: 'Edit Users', description: 'Modify user details' },
      { key: FEATURE_PERMISSIONS.USER_DELETE, label: 'Delete Users', description: 'Remove users' },
      { key: FEATURE_PERMISSIONS.USER_CHANGE_ROLE, label: 'Change User Roles', description: 'Assign roles to users' },
      { key: FEATURE_PERMISSIONS.USER_MANAGE_PERMISSIONS, label: 'Manage Permissions', description: 'Grant/revoke permissions' },
      { key: FEATURE_PERMISSIONS.USER_MANAGE_ATTRIBUTES, label: 'Manage User Attributes', description: 'Edit Title, Department, Display Name (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.USER_MANAGE_TERRITORIES, label: 'Manage Territories', description: 'Create territories and assign them to sales reps (ADMIN ONLY)' },
      { key: FEATURE_PERMISSIONS.USER_VIEW_ALL, label: 'View All Users', description: 'View all users in system (ADMIN ONLY)' },
    ],
  },
  {
    label: 'Role Management',
    description: 'Role and permission management',
    permissions: [
      { key: PAGE_PERMISSIONS.ROLE_MANAGEMENT, label: 'Role Management Page', description: 'Access the role management page' },
      { key: FEATURE_PERMISSIONS.ROLE_CREATE, label: 'Create Roles', description: 'Add new roles' },
      { key: FEATURE_PERMISSIONS.ROLE_EDIT, label: 'Edit Roles', description: 'Modify role permissions' },
      { key: FEATURE_PERMISSIONS.ROLE_DELETE, label: 'Delete Roles', description: 'Remove roles' },
    ],
  },
  {
    label: 'Workflow Automation',
    description: 'Automated task creation workflows',
    permissions: [
      { key: FEATURE_PERMISSIONS.WORKFLOWS_MANAGE, label: 'Manage Workflows', description: 'Configure opportunity stage workflows' },
      { key: FEATURE_PERMISSIONS.WORKFLOWS_VIEW, label: 'View Workflows', description: 'View workflow configurations' },
    ],
  },
  {
    label: 'AI Chatbot',
    description: 'Chatbot feedback and chat history',
    permissions: [
      { key: FEATURE_PERMISSIONS.CHATBOT_VIEW, label: 'View Chatbot', description: 'View chatbot feedback and chat history (read-only)' },
      { key: FEATURE_PERMISSIONS.CHATBOT_MANAGE, label: 'Manage Chatbot', description: 'Respond to feedback and clear chat history' },
    ],
  },
  {
    label: 'Knowledge Management',
    description: 'Knowledge base articles, FAQs, and support page content',
    permissions: [
      { key: FEATURE_PERMISSIONS.KNOWLEDGE_VIEW, label: 'View Knowledge Base', description: 'View knowledge base articles, FAQs, and support sections (read-only)' },
      { key: FEATURE_PERMISSIONS.KNOWLEDGE_MANAGE, label: 'Manage Knowledge Base', description: 'Create, edit, and delete knowledge base articles, FAQs, and support sections' },
    ],
  },
  {
    label: 'Achievements & Gamification',
    description: 'User achievements, points, levels, and gamification maintenance',
    permissions: [
      { key: FEATURE_PERMISSIONS.ACHIEVEMENTS_VIEW, label: 'View Achievements', description: 'View achievements, points, levels, and gamification data (read-only)' },
      { key: FEATURE_PERMISSIONS.ACHIEVEMENTS_MANAGE, label: 'Manage Achievements', description: 'Adjust points, grant achievements, edit levels, manage gamification data' },
    ],
  },
  {
    label: 'Notes',
    description: 'Cross-entity note operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.NOTE_ADD, label: 'Add Notes', description: 'Add notes to any entity (customers, opportunities, tasks, contacts) without requiring full edit permission' },
      { key: FEATURE_PERMISSIONS.NOTE_DELETE_ANY, label: 'Delete Any Notes', description: 'Delete any user\'s notes on any entity (ADMIN ONLY). All users can always delete their own notes.' },
    ],
  },
  {
    label: 'Log Management',
    description: 'System and entity log viewing and cleanup',
    permissions: [
      { key: FEATURE_PERMISSIONS.LOGS_VIEW, label: 'View Logs', description: 'View system logs, entity change logs, and security logs (read-only)' },
      { key: FEATURE_PERMISSIONS.LOGS_MANAGE, label: 'Manage Logs', description: 'Run log cleanup and maintenance operations' },
    ],
  },
  {
    label: 'Notification Settings',
    description: 'In-app and email notification configuration',
    permissions: [
      { key: FEATURE_PERMISSIONS.NOTIFICATIONS_VIEW, label: 'View Notification Settings', description: 'View notification configuration (read-only)' },
      { key: FEATURE_PERMISSIONS.NOTIFICATIONS_MANAGE, label: 'Manage Notification Settings', description: 'Test and configure notification delivery' },
    ],
  },
  {
    label: 'AI Sales Agent (Add-on)',
    description: 'Customer health insights and recommendations. Only active when the AI Sales Agent add-on is enabled.',
    permissions: [
      { key: PAGE_PERMISSIONS.RECOMMENDATIONS, label: 'Recommendations Page', description: 'Access the Recommendations queue page' },
      { key: FEATURE_PERMISSIONS.AI_RECS_VIEW, label: 'View Recommendations', description: 'See the Recommendations tab on customers and own queue' },
      { key: FEATURE_PERMISSIONS.AI_RECS_ACT, label: 'Act on Recommendations', description: 'Accept, dismiss, or snooze recommendations' },
      { key: FEATURE_PERMISSIONS.AI_RECS_GENERATE, label: 'Refresh Recommendations', description: 'Run an on-demand refresh for a customer (rate limited)' },
      { key: FEATURE_PERMISSIONS.AI_RECS_VIEW_TEAM, label: 'View Team Recommendations', description: 'See recommendations for all reps\' customers (managers)' },
      { key: FEATURE_PERMISSIONS.QUOTES_BUILD, label: 'Build Quotes', description: 'Build and edit quote drafts from the product catalog for customers you can see' },
    ],
  },
  {
    label: 'System Administration',
    description: 'System-level settings and operations',
    permissions: [
      { key: FEATURE_PERMISSIONS.SETTINGS_VIEW, label: 'View Settings', description: 'View general system settings (read-only)' },
      { key: FEATURE_PERMISSIONS.SETTINGS_MANAGE, label: 'Manage Settings', description: 'Configure general system settings' },
      { key: FEATURE_PERMISSIONS.SYSTEM_SETTINGS_VIEW, label: 'View System Settings', description: 'View email templates, script variables, configs (read-only)' },
      { key: FEATURE_PERMISSIONS.SYSTEM_SETTINGS_MANAGE, label: 'Manage System Settings', description: 'Edit email templates, script variables, configs (ADMIN ONLY)' },
      { key: ACTION_PERMISSIONS.BULK_OPERATIONS, label: 'Bulk Operations', description: 'Perform bulk updates/deletes' },
      { key: ACTION_PERMISSIONS.DESTRUCTIVE_IMPORT, label: 'Destructive Import', description: 'Import with deletions' },
      { key: ACTION_PERMISSIONS.MANAGE_BUSINESS_TYPES, label: 'Manage Business Types', description: 'Manage business type options' },
      { key: ACTION_PERMISSIONS.MANAGE_LEAD_SOURCES, label: 'Manage Lead Sources', description: 'Manage lead source options' },
      { key: ACTION_PERMISSIONS.MANAGE_CATEGORIES, label: 'Manage Categories', description: 'Manage template categories' },
      { key: ACTION_PERMISSIONS.VIEW_CHANGE_LOGS, label: 'View Change Logs', description: 'See entity change history' },
      { key: ACTION_PERMISSIONS.VIEW_USER_ACTIVITY, label: 'View User Activity', description: 'Monitor user actions' },
      { key: ACTION_PERMISSIONS.API_ACCESS, label: 'API Access', description: 'Access external API integrations' },
      { key: FEATURE_PERMISSIONS.DATA_SYNC_VIEW, label: 'View Data Sync', description: 'View sync status, logs, and history (read-only)' },
      { key: FEATURE_PERMISSIONS.DATA_SYNC_TRIGGER, label: 'Trigger Data Sync', description: 'Start quick and full data syncs' },
      { key: FEATURE_PERMISSIONS.DATA_SYNC_HISTORICAL, label: 'Trigger Historical Sync', description: 'Start historical force-write syncs (destructive)' },
    ],
  },
];

// ==================== DEFAULT ROLE PERMISSIONS ====================

/**
 * Default permission sets for standard roles
 */
export const DEFAULT_ROLE_PERMISSIONS = {
  admin: Object.values(ALL_PERMISSIONS), // Admins get everything
  
  manager: [
    // Pages
    PAGE_PERMISSIONS.DASHBOARD,
    PAGE_PERMISSIONS.CUSTOMERS,
    PAGE_PERMISSIONS.OPPORTUNITIES,
    PAGE_PERMISSIONS.ORDERS,
    PAGE_PERMISSIONS.CONTACTS,
    PAGE_PERMISSIONS.TASKS,
    PAGE_PERMISSIONS.CALENDAR,
    PAGE_PERMISSIONS.REPORTS,
    PAGE_PERMISSIONS.EMAIL_CAMPAIGNS,
    PAGE_PERMISSIONS.EMAIL_TEMPLATES,
    PAGE_PERMISSIONS.EMAIL_SEGMENTS,
    PAGE_PERMISSIONS.EMAIL_ANALYTICS,
    PAGE_PERMISSIONS.SALES_REPS,
    
    // Customer Features
    FEATURE_PERMISSIONS.CUSTOMER_CREATE,
    FEATURE_PERMISSIONS.CUSTOMER_EDIT,
    FEATURE_PERMISSIONS.CUSTOMER_DELETE,
    FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL,
    FEATURE_PERMISSIONS.CUSTOMER_IMPORT,
    FEATURE_PERMISSIONS.CUSTOMER_EXPORT,
    FEATURE_PERMISSIONS.CUSTOMER_MERGE,
    
    // Opportunity Features
    FEATURE_PERMISSIONS.OPP_CREATE,
    FEATURE_PERMISSIONS.OPP_EDIT,
    FEATURE_PERMISSIONS.OPP_DELETE,
    FEATURE_PERMISSIONS.OPP_VIEW_ALL,
    FEATURE_PERMISSIONS.OPP_IMPORT,
    FEATURE_PERMISSIONS.OPP_EXPORT,
    FEATURE_PERMISSIONS.OPP_FOLLOWUP_MANAGE,
    
    // AI Sales Agent
    PAGE_PERMISSIONS.RECOMMENDATIONS,
    FEATURE_PERMISSIONS.AI_RECS_VIEW,
    FEATURE_PERMISSIONS.AI_RECS_ACT,
    FEATURE_PERMISSIONS.AI_RECS_GENERATE,
    FEATURE_PERMISSIONS.AI_RECS_VIEW_TEAM,
    FEATURE_PERMISSIONS.QUOTES_BUILD,
    
    // Order Features
    FEATURE_PERMISSIONS.ORDER_CREATE,
    FEATURE_PERMISSIONS.ORDER_EDIT,
    FEATURE_PERMISSIONS.ORDER_VIEW_ALL,
    FEATURE_PERMISSIONS.ORDER_VIEW_FINANCIALS,
    FEATURE_PERMISSIONS.ORDER_EXPORT,
    
    // Contact Features
    FEATURE_PERMISSIONS.CONTACT_CREATE,
    FEATURE_PERMISSIONS.CONTACT_EDIT,
    FEATURE_PERMISSIONS.CONTACT_DELETE,
    FEATURE_PERMISSIONS.CONTACT_EXPORT,
    FEATURE_PERMISSIONS.CONTACT_MERGE,
    
    // Task Features
    FEATURE_PERMISSIONS.TASK_CREATE,
    FEATURE_PERMISSIONS.TASK_EDIT,
    FEATURE_PERMISSIONS.TASK_DELETE,
    FEATURE_PERMISSIONS.TASK_EXPORT,
    FEATURE_PERMISSIONS.TASK_VIEW_ALL,
    FEATURE_PERMISSIONS.TASK_ASSIGN_ANY,
    
    // Calendar Features
    FEATURE_PERMISSIONS.CALENDAR_CREATE,
    FEATURE_PERMISSIONS.CALENDAR_EDIT,
    FEATURE_PERMISSIONS.CALENDAR_DELETE,
    FEATURE_PERMISSIONS.CALENDAR_VIEW_ALL,
    
    // Email Marketing
    FEATURE_PERMISSIONS.EMAIL_CREATE_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_EDIT_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_DELETE_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_SEND_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_CREATE_TEMPLATE,
    FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE,
    FEATURE_PERMISSIONS.EMAIL_CREATE_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_EDIT_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_DELETE_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_VIEW_ANALYTICS,
    
    // Reports
    FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS,
    FEATURE_PERMISSIONS.REPORTS_EXPORT,
    
    // Settings
    FEATURE_PERMISSIONS.SETTINGS_MANAGE,
    
    // Workflow Automation
    FEATURE_PERMISSIONS.WORKFLOWS_MANAGE,
    FEATURE_PERMISSIONS.WORKFLOWS_VIEW,
    
    // Chatbot & Knowledge
    FEATURE_PERMISSIONS.CHATBOT_MANAGE,
    FEATURE_PERMISSIONS.KNOWLEDGE_MANAGE,
    
    // Achievements & Gamification
    FEATURE_PERMISSIONS.ACHIEVEMENTS_MANAGE,
    
    // Logs & Notifications
    FEATURE_PERMISSIONS.LOGS_VIEW,
    FEATURE_PERMISSIONS.NOTIFICATIONS_MANAGE,
    
    // Notes
    FEATURE_PERMISSIONS.NOTE_DELETE_ANY,
    
    // Actions
    ACTION_PERMISSIONS.VIEW_CHANGE_LOGS,
    ACTION_PERMISSIONS.BULK_OPERATIONS,
  ],
  marketing: [
    // Pages
    PAGE_PERMISSIONS.DASHBOARD,
    PAGE_PERMISSIONS.CUSTOMERS,
    PAGE_PERMISSIONS.CONTACTS,
    PAGE_PERMISSIONS.EMAIL_CAMPAIGNS,
    PAGE_PERMISSIONS.EMAIL_TEMPLATES,
    PAGE_PERMISSIONS.EMAIL_SEGMENTS,
    PAGE_PERMISSIONS.EMAIL_ANALYTICS,
    
    // Customer Features (read-only mostly)
    FEATURE_PERMISSIONS.CUSTOMER_EXPORT,
    
    // Contact Features
    FEATURE_PERMISSIONS.CONTACT_CREATE,
    FEATURE_PERMISSIONS.CONTACT_EDIT,
    FEATURE_PERMISSIONS.CONTACT_EXPORT,
    
    // Email Marketing (full access)
    FEATURE_PERMISSIONS.EMAIL_CREATE_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_EDIT_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_DELETE_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_SEND_CAMPAIGN,
    FEATURE_PERMISSIONS.EMAIL_CREATE_TEMPLATE,
    FEATURE_PERMISSIONS.EMAIL_EDIT_TEMPLATE,
    FEATURE_PERMISSIONS.EMAIL_CREATE_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_EDIT_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_DELETE_SEGMENT,
    FEATURE_PERMISSIONS.EMAIL_VIEW_ANALYTICS,
  ],
  
  user: [
    // Pages
    PAGE_PERMISSIONS.DASHBOARD,
    PAGE_PERMISSIONS.CUSTOMERS,
    PAGE_PERMISSIONS.OPPORTUNITIES,
    PAGE_PERMISSIONS.ORDERS,
    PAGE_PERMISSIONS.CONTACTS,
    PAGE_PERMISSIONS.TASKS,
    PAGE_PERMISSIONS.CALENDAR,
    
    // Customer Features (assigned only)
    FEATURE_PERMISSIONS.CUSTOMER_EDIT,
    FEATURE_PERMISSIONS.CUSTOMER_EXPORT,
    
    // Opportunity Features (assigned only)
    FEATURE_PERMISSIONS.OPP_CREATE,
    FEATURE_PERMISSIONS.OPP_EDIT,
    FEATURE_PERMISSIONS.OPP_EXPORT,
    FEATURE_PERMISSIONS.OPP_FOLLOWUP_MANAGE,
    
    // Order Features (assigned only)
    FEATURE_PERMISSIONS.ORDER_EXPORT,
    
    // Contact Features
    FEATURE_PERMISSIONS.CONTACT_CREATE,
    FEATURE_PERMISSIONS.CONTACT_EDIT,
    FEATURE_PERMISSIONS.CONTACT_EXPORT,
    
    // Task Features (own tasks only)
    FEATURE_PERMISSIONS.TASK_CREATE,
    FEATURE_PERMISSIONS.TASK_EDIT,
    FEATURE_PERMISSIONS.TASK_DELETE,
    FEATURE_PERMISSIONS.TASK_EXPORT,
    
    // Calendar Features (own events)
    FEATURE_PERMISSIONS.CALENDAR_CREATE,
    FEATURE_PERMISSIONS.CALENDAR_EDIT,
    FEATURE_PERMISSIONS.CALENDAR_DELETE,
    
    // Notes
    FEATURE_PERMISSIONS.NOTE_ADD,
    
    // AI Sales Agent (own customers)
    PAGE_PERMISSIONS.RECOMMENDATIONS,
    FEATURE_PERMISSIONS.AI_RECS_VIEW,
    FEATURE_PERMISSIONS.AI_RECS_ACT,
    FEATURE_PERMISSIONS.AI_RECS_GENERATE,
    FEATURE_PERMISSIONS.QUOTES_BUILD,
  ],
  
  disabled: [],  // Disabled role - no permissions, blocks login

  adminViewOnly: [
    // Read-only admin role ("Admin - View Only") - can view all data like admin, but cannot create/edit/delete anything
    // Pages (same as admin - includes admin pages for viewing)
    PAGE_PERMISSIONS.DASHBOARD,
    PAGE_PERMISSIONS.CUSTOMERS,
    PAGE_PERMISSIONS.OPPORTUNITIES,
    PAGE_PERMISSIONS.ORDERS,
    PAGE_PERMISSIONS.CONTACTS,
    PAGE_PERMISSIONS.TASKS,
    PAGE_PERMISSIONS.REPORTS,
    PAGE_PERMISSIONS.EMAIL_CAMPAIGNS,
    PAGE_PERMISSIONS.EMAIL_TEMPLATES,
    PAGE_PERMISSIONS.EMAIL_SEGMENTS,
    PAGE_PERMISSIONS.EMAIL_ANALYTICS,
    PAGE_PERMISSIONS.SALES_REPS,
    PAGE_PERMISSIONS.CALENDAR,
    PAGE_PERMISSIONS.USER_ADMIN,
    PAGE_PERMISSIONS.ROLE_MANAGEMENT,
    
    // View-only permissions (no CREATE/EDIT/DELETE)
    FEATURE_PERMISSIONS.CALENDAR_VIEW_ALL,
    FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL,
    FEATURE_PERMISSIONS.CUSTOMER_EXPORT,
    FEATURE_PERMISSIONS.OPP_VIEW_ALL,
    FEATURE_PERMISSIONS.OPP_EXPORT,
    FEATURE_PERMISSIONS.ORDER_VIEW_ALL,
    FEATURE_PERMISSIONS.ORDER_VIEW_FINANCIALS,
    FEATURE_PERMISSIONS.ORDER_EXPORT,
    FEATURE_PERMISSIONS.CONTACT_VIEW_ALL,
    FEATURE_PERMISSIONS.CONTACT_EXPORT,
    FEATURE_PERMISSIONS.TASK_VIEW_ALL,
    FEATURE_PERMISSIONS.TASK_EXPORT,
    FEATURE_PERMISSIONS.SALES_REP_VIEW_ALL,
    FEATURE_PERMISSIONS.EMAIL_VIEW_ANALYTICS,
    FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS,
    FEATURE_PERMISSIONS.REPORTS_EXPORT,
    FEATURE_PERMISSIONS.REPORTS_CACHE_REFRESH,
    FEATURE_PERMISSIONS.WORKFLOWS_VIEW,
    FEATURE_PERMISSIONS.USER_VIEW_ALL,
    FEATURE_PERMISSIONS.SETTINGS_VIEW,
    FEATURE_PERMISSIONS.SYSTEM_SETTINGS_VIEW,
    FEATURE_PERMISSIONS.CHATBOT_VIEW,
    FEATURE_PERMISSIONS.KNOWLEDGE_VIEW,
    FEATURE_PERMISSIONS.ACHIEVEMENTS_VIEW,
    FEATURE_PERMISSIONS.LOGS_VIEW,
    FEATURE_PERMISSIONS.NOTIFICATIONS_VIEW,
    FEATURE_PERMISSIONS.DATA_SYNC_VIEW,
    PAGE_PERMISSIONS.RECOMMENDATIONS,
    FEATURE_PERMISSIONS.AI_RECS_VIEW,
    FEATURE_PERMISSIONS.AI_RECS_VIEW_TEAM,
    
    // System
    ACTION_PERMISSIONS.VIEW_CHANGE_LOGS,
    ACTION_PERMISSIONS.VIEW_USER_ACTIVITY,
  ],
};

// ==================== PERMISSION UTILITIES ====================

/**
 * Check if a user has a specific permission
 * @param {Object} user - User object with role and permissions
 * @param {string} permission - Permission key to check
 * @param {Object} rolePermissions - Map of role -> permissions array
 * @returns {boolean}
 */
export function hasPermission(user, permission, rolePermissions = {}) {
  if (!user) return false;
  
  // Check user-specific permission denials first (applies to ALL roles including admin)
  if (user.permissionDenials && Array.isArray(user.permissionDenials)) {
    if (user.permissionDenials.includes(permission)) {
      // Granular RBAC: even if a page permission is explicitly denied, the user still
      // gains access if they have at least one accessible feature permission within
      // that page. Feature grants are always more specific than page-level denials.
      if (permission.startsWith('page:')) {
        const childFeatures = PAGE_FEATURE_MAP[permission] || [];
        if (childFeatures.some(feature => hasPermission(user, feature, rolePermissions))) {
          return true;
        }
      }
      return false;
    }
  }
  
  // Super admin check (admin role has all permissions unless explicitly denied above)
  if (user.role === 'admin') return true;
  
  // Check user-specific permission overrides (grants)
  if (user.permissionGrants && Array.isArray(user.permissionGrants)) {
    if (user.permissionGrants.includes(permission)) return true;
  }
  
  // Check role-based permissions
  const userRole = user.role || 'user';
  const rolePerms = rolePermissions[userRole] || DEFAULT_ROLE_PERMISSIONS[userRole] || [];
  
  if (rolePerms.includes(permission)) return true;
  
  // Granular RBAC fallback for page permissions: if the page isn't in the user's role
  // but they have any feature permission for that page, grant access to the page.
  if (permission.startsWith('page:')) {
    const childFeatures = PAGE_FEATURE_MAP[permission] || [];
    return childFeatures.some(feature => hasPermission(user, feature, rolePermissions));
  }
  
  return false;
}

/**
 * Check if a user has ANY of the specified permissions
 * @param {Object} user - User object
 * @param {Array<string>} permissions - Array of permission keys
 * @param {Object} rolePermissions - Map of role -> permissions array
 * @returns {boolean}
 */
export function hasAnyPermission(user, permissions, rolePermissions = {}) {
  return permissions.some(perm => hasPermission(user, perm, rolePermissions));
}

/**
 * Check if a user has ALL of the specified permissions
 * @param {Object} user - User object
 * @param {Array<string>} permissions - Array of permission keys
 * @param {Object} rolePermissions - Map of role -> permissions array
 * @returns {boolean}
 */
export function hasAllPermissions(user, permissions, rolePermissions = {}) {
  return permissions.every(perm => hasPermission(user, perm, rolePermissions));
}

/**
 * Get all effective permissions for a user (role + grants - denials)
 * @param {Object} user - User object
 * @param {Object} rolePermissions - Map of role -> permissions array
 * @returns {Array<string>}
 */
export function getUserPermissions(user, rolePermissions = {}) {
  if (!user) return [];
  
  // Start with role permissions
  const userRole = user.role || 'user';
  const rolePerms = user.role === 'admin'
    ? Object.values(ALL_PERMISSIONS)
    : (rolePermissions[userRole] || DEFAULT_ROLE_PERMISSIONS[userRole] || []);
  const perms = new Set(rolePerms);
  
  // Add grants
  if (user.permissionGrants && Array.isArray(user.permissionGrants)) {
    user.permissionGrants.forEach(p => perms.add(p));
  }
  
  // Remove denials
  if (user.permissionDenials && Array.isArray(user.permissionDenials)) {
    user.permissionDenials.forEach(p => perms.delete(p));
  }
  
  return Array.from(perms);
}

/**
 * Check if user is in a read-only admin role (Admin - View Only)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function isViewOnlyRole(user) {
  return user?.role === 'adminViewOnly';
}

/**
 * Check if user has the disabled role
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function isDisabledRole(user) {
  return user?.role === 'disabled';
}

/**
 * Check if user can modify data (create/edit/delete operations)
 * Admin - View Only role returns false even though they can see all data
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function canModifyData(user) {
  if (!user) return false;
  // Admin - View Only role cannot modify data despite seeing everything
  if (user.role === 'adminViewOnly') return false;
  return true;
}

/**
 * Check if user should see all data (not filtered by displayName)
 * Admin and Admin - View Only both see all data, but only Admin can modify.
 * For custom role users, use can(FEATURE_PERMISSIONS.X) from usePermissions alongside this check.
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function canViewAllData(user) {
  if (!user) return false;
  return user.role === 'admin' || user.role === 'adminViewOnly';
}

/**
 * Check if user can see all sales reps' data in reports
 * True for admin/adminViewOnly AND for users with REPORTS_VIEW_ALL_REPS permission (e.g. manager role).
 * For custom role users with entity-specific VIEW_ALL permissions, use can(FEATURE_PERMISSIONS.X)
 * from usePermissions alongside this check.
 * @param {Object} user - User object
 * @returns {boolean}
 */
export function canViewAllReports(user) {
  if (!user) return false;
  return canViewAllData(user) || hasPermission(user, FEATURE_PERMISSIONS.REPORTS_VIEW_ALL_REPS);
}

/**
 * Get permission label from key
 * @param {string} permissionKey - Permission key
 * @returns {string}
 */
export function getPermissionLabel(permissionKey) {
  for (const group of PERMISSION_GROUPS) {
    const perm = group.permissions.find(p => p.key === permissionKey);
    if (perm) return perm.label;
  }
  return permissionKey;
}

/**
 * Get permission description from key
 * @param {string} permissionKey - Permission key
 * @returns {string}
 */
export function getPermissionDescription(permissionKey) {
  for (const group of PERMISSION_GROUPS) {
    const perm = group.permissions.find(p => p.key === permissionKey);
    if (perm) return perm.description;
  }
  return '';
}

/**
 * Filter permissions by category (page, feature, action)
 * @param {Array<string>} permissions - Array of permission keys
 * @param {string} category - 'page', 'feature', 'action'
 * @returns {Array<string>}
 */
export function filterPermissionsByCategory(permissions, category) {
  const prefix = `${category}:`;
  return permissions.filter(p => p.startsWith(prefix));
}

/**
 * Build the set of names used for RBAC filtering (orders, customers, contacts).
 * Checks both displayName and full_name so that partial displayNames
 * (e.g. "Donna") still match commission/rep names ("Donna Smith").
 * 
 * @param {Object} user - User object with displayName, full_name, firstName, lastName
 * @returns {string[]} Lowercase-trimmed names to match against data
 */
export function getRbacMatchNames(user) {
  const names = new Set();
  if (user?.displayName) names.add(user.displayName.toLowerCase().trim());
  if (user?.full_name) names.add(user.full_name.toLowerCase().trim());
  // Also try firstName + lastName if both exist and produce a different result
  if (user?.firstName && user?.lastName) {
    names.add(`${user.firstName} ${user.lastName}`.toLowerCase().trim());
  }
  // Remove empty strings
  names.delete('');
  return [...names];
}
